import AWS from 'aws-sdk';
import {
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUser,
} from 'amazon-cognito-identity-js';

import config from '../../config';

import * as status from './status';
import * as types from './types';
import { notify, LEVEL_ERROR, setLoggerUser } from '../../utils/logger';

export const COGNITO_KEY_MAP = [
  { key: 'profileID', cognitoName: 'custom:profile_id' },
  { key: 'email', cognitoName: 'email' },
  { key: 'name', cognitoName: 'name' },
  { key: 'mobile', cognitoName: 'phone_number' },
  { key: 'nric', cognitoName: 'custom:nric' },
  { key: 'icFront', cognitoName: 'custom:ic_front' },
  { key: 'icBack', cognitoName: 'custom:ic_back' },
  { key: 'incomeID', cognitoName: 'custom:income_id' },
  { key: 'incomeNetCents', cognitoName: 'custom:income_net_cents' },
];

export function mapUserAttributes(attributes, reverse = false) {
  const user = {};
  COGNITO_KEY_MAP.forEach((k) => {
    if (!reverse) {
      const attr = attributes.find(a => a.Name === k.cognitoName);
      if (attr) {
        user[k.key] = attr.Value;
      }
    } else {
      const attr = attributes.find(a => a.key === k.key);
      if (attr) {
        user[k.cognitoName] = attr.value;
      }
    }
  });
  return user;
}

export function generateCognitoUser(username) {
  const userPool = new CognitoUserPool({
    UserPoolId: config.cognito.USER_POOL_ID,
    ClientId: config.cognito.APP_CLIENT_ID,
  });
  return new CognitoUser({ Username: username, Pool: userPool });
}

function userSessionData(user) {
  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      const token = session.getIdToken().getJwtToken();
      user.getUserAttributes((attrErr, attributes) => {
        if (attrErr) {
          reject(attrErr);
          return;
        }
        const userAttr = mapUserAttributes(attributes);
        setLoggerUser(userAttr.email);
        resolve({ token, user: userAttr });
      });
    });
  });
}

function cognitoAuthenticate(username, password, authToken = null) {
  const user = generateCognitoUser(username);

  const authenticationData = {
    Username: username,
    Password: password,
  };
  const authenticationDetails = new AuthenticationDetails(authenticationData);

  if (authToken) {
    user.setAuthenticationFlowType('CUSTOM_AUTH');
  }
  return new Promise((resolve, reject) => (
    user.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const token = result.getIdToken().getJwtToken();
        user.getUserAttributes((err, attributes) => {
          const userAttr = mapUserAttributes(attributes);
          resolve({ token, user: userAttr });
        });
      },
      onFailure: err => reject(err),
      customChallenge: function customChallenge() {
        user.sendCustomChallengeAnswer(authToken, this);
      },
    })
  ));
}

function authenticateRequest() {
  return {
    type: types.AUTHENTICATE_REQUEST,
  };
}

function authenticateSuccess(token, user, fromFrame = false) {
  return {
    type: types.AUTHENTICATE_SUCCESS,
    payload: {
      token,
      user,
      fromFrame,
    },
  };
}

function authenticateFailure(message) {
  return {
    type: types.AUTHENTICATE_FAILURE,
    payload: {
      statusText: message,
      user: null,
    },
  };
}

function currentUserRequest() {
  return {
    type: types.CURRENT_USER_REQUEST,
  };
}

function currentUserFailure() {
  return {
    type: types.CURRENT_USER_FAILURE,
  };
}

function currentUserSuccess() {
  return {
    type: types.CURRENT_USER_SUCCESS,
  };
}

function currentUserError(message) {
  return {
    type: types.CURRENT_USER_ERROR,
    payload: {
      statusText: message,
    },
  };
}

export function authenticate(username, password, authToken = null) {
  return async (dispatch) => {
    dispatch(authenticateRequest());
    let user = null;
    let token = null;
    try {
      ({ token, user } = await cognitoAuthenticate(username, password, authToken));
    } catch (e) {
      dispatch(authenticateFailure(e.message));
      notify(e.message, LEVEL_ERROR, 'Failed Authentication');
      if (e.message === 'User is not confirmed.') {
        return status.AUTHENTICATE_STATUS_NOT_CONFIRMED;
      }
      if (e.message === 'Incorrect username or password.') {
        return status.AUTHENTICATE_STATUS_INVALID;
      }
      if (e.message === 'User does not exist.') {
        return status.AUTHENTICATE_STATUS_NOT_EXIST;
      }
      if (e.message.includes('Missing required parameter')) {
        return status.AUTHENTICATE_STATUS_MISSING;
      }
      return status.AUTHENTICATE_STATUS_ERROR;
    }
    let fromFrame = false;
    if (authToken) {
      fromFrame = true;
    }
    dispatch(authenticateSuccess(token, user, fromFrame));
    return status.AUTHENTICATE_STATUS_OK;
  };
}

export function getCurrentUser() {
  return async (dispatch) => {
    dispatch(currentUserRequest());
    const userPool = new CognitoUserPool({
      UserPoolId: config.cognito.USER_POOL_ID,
      ClientId: config.cognito.APP_CLIENT_ID,
    });
    try {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        dispatch(currentUserFailure());
        return null;
      }
      dispatch(currentUserSuccess());
      return currentUser;
    } catch (e) {
      notify(e.message, LEVEL_ERROR, 'Failed to get current user token.');
      return null;
    }
  };
}

export function getSessionData() {
  return async (dispatch) => {
    const currentUser = await dispatch(getCurrentUser());
    if (!currentUser) {
      return null;
    }
    let sessionData;
    try {
      sessionData = await userSessionData(currentUser);
    } catch (e) {
      dispatch(currentUserError(e.message));
      return null;
    }
    return sessionData;
  };
}

export function authenticateCurrentUser() {
  return async (dispatch) => {
    const sessionData = await dispatch(getSessionData());
    if (!sessionData) {
      dispatch(authenticateFailure('User session not available.'));
      return null;
    }
    dispatch(authenticateSuccess(sessionData.token, sessionData.user));
    return sessionData.token;
  };
}

function authenticateLogoutSuccess() {
  return {
    type: types.AUTHENTICATE_LOGOUT_SUCCESS,
  };
}

export function authenticateLogout() {
  return async (dispatch) => {
    const currentUser = await dispatch(getCurrentUser());
    if (currentUser) {
      currentUser.signOut();
    }
    if (AWS.config.credentials) {
      AWS.config.credentials.clearCachedId();
    }
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({ });
    dispatch(authenticateLogoutSuccess());
  };
}



// WEBPACK FOOTER //
// ./src/redux/authenticate/actions.js